import 'lib/assets/fonts.scss'

import { mix, transparentize } from 'polished'
import { createContext, ReactNode, useContext, useMemo, useState } from 'react'

import styled, { ThemedProvider } from './styled'
import { Colors, ComputedTheme, Theme } from './theme'

export default styled
export * from './dynamic'
export * from './layer'
export * from './styled'
export * from './theme'
export * as ThemedText from './type'

const white = 'hsl(0, 0%, 100%)'
const black = 'hsl(0, 0%, 0%)'

const brandLight = '#B50028'
const brandDark = '#B50028'
export const brand = brandLight

const stateColors = {
  active: 'hsl(215, 79%, 51.4%)',
  success: 'hsl(145, 63.4%, 41.8%)',
  warning: 'hsl(43, 89.9%, 53.5%)',
  error: 'hsl(0, 98%, 62.2%)',
}

export const lightTheme: Colors = {
  // surface
  accent: brandLight,
  container: 'rgb(255, 255, 255)',
  module: 'rgb(255, 255, 255)',
  interactive: 'transparent',
  outline: 'hsl(225, 7%, 78%)',
  dialog: 'white',
  scrollbar: 'hsl(224, 9%, 57%)',
  tokenOnHover: 'rgb(244, 244, 244)',
  separator: 'rgb(237, 238, 242)',
  actionButton: 'rgb(237, 238, 242)',
  actionOnHover: 'rgb(156, 0, 34)',
  inputOnHover: 'rgb(244, 244, 244)',

  // text
  onAccent: white,
  primary: black,
  secondary: 'hsl(227, 10%, 37.5%)',
  hint: 'hsl(224, 9%, 57%)',
  onInteractive: black,
  disabledText: 'rgb(86, 90, 105)',

  // state
  ...stateColors,

  currentColor: 'currentColor',
}

export const darkTheme: Colors = {
  // surface
  accent: brandDark,
  container: 'rgb(25, 27, 31)',
  module: 'rgb(25, 27, 31)',
  interactive: 'transparent',
  outline: 'hsl(227, 10%, 37.5%)',
  dialog: black,
  scrollbar: 'hsl(225, 10%, 47.1%)',
  tokenOnHover: 'rgb(33, 36, 41)',
  separator: 'rgb(44, 47, 54)',
  actionButton: 'rgb(44, 47, 54)',
  actionOnHover: 'rgb(156, 0, 34)',
  inputOnHover: 'rgb(33, 36, 41)',

  // text
  onAccent: white,
  primary: white,
  secondary: 'hsl(224, 8.7%, 57.1%)',
  hint: 'hsl(225, 10%, 47.1%)',
  onInteractive: white,
  disabledText: 'rgb(195, 197, 203)',

  // state
  ...stateColors,

  currentColor: 'currentColor',
}

export const defaultTheme = {
  borderRadius: 0,
  fontFamily: '"Montserrat", sans-serif',
  fontFamilyVariable: '"Montserrat", sans-serif',
  fontFamilyCode: '"Montserrat", sans-serif',
  ...lightTheme,
}

export function useSystemTheme() {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)')
  const [systemTheme, setSystemTheme] = useState(prefersDark.matches ? darkTheme : lightTheme)
  prefersDark.addEventListener('change', (e) => {
    setSystemTheme(e.matches ? darkTheme : lightTheme)
  })
  return systemTheme
}

const ThemeContext = createContext<ComputedTheme>(toComputedTheme(defaultTheme))

interface ThemeProviderProps {
  theme?: Theme
  children: ReactNode
}

export function ThemeProvider({ theme, children }: ThemeProviderProps) {
  const contextTheme = useContext(ThemeContext)
  const value = useMemo(() => {
    return toComputedTheme({
      ...contextTheme,
      ...theme,
    } as Required<Theme>)
  }, [contextTheme, theme])
  return (
    <ThemeContext.Provider value={value}>
      <ThemedProvider theme={value}>{children}</ThemedProvider>
    </ThemeContext.Provider>
  )
}

function toComputedTheme(theme: Required<Theme>): ComputedTheme {
  return {
    ...theme,
    borderRadius: clamp(
      Number.isFinite(theme.borderRadius) ? (theme.borderRadius as number) : theme.borderRadius ? 1 : 0
    ),
    onHover: (color: string) =>
      color === theme.primary ? transparentize(0.2, theme.primary) : mix(0.16, theme.primary, color),
  }

  function clamp(value: number) {
    return Math.min(Math.max(value, 0), 1)
  }
}
